// src/services/socketService.js
import { io } from "socket.io-client";

const SOCKET_URL =
    import.meta.env.VITE_SOCKET_URL?.trim() || "https://api.theglobalapp.com";

class SocketService {
    constructor() {
        this.socket = null;
        this.connected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this._token = null;
    }

    connect(token, onConnected) {
        if (!SOCKET_URL) {
            console.warn("⚠️ Socket URL missing (VITE_SOCKET_URL)");
            return null;
        }

        if (this.socket?.connected) {
            if (token && token !== this._token) {
                this.disconnect();
            } else {
                if (onConnected) onConnected();
                return this.socket;
            }
        }

        // ✅ Guard: if a socket already exists but is still connecting, reuse it.
        // React StrictMode runs effects twice, which without this guard causes a
        // second socket to overwrite this.socket before the first one connects.
        // The result is that the first socket's onConnected callback calls
        // socketService.emit() against the NEW (unconnected) this.socket and fails.
        if (this.socket && !this.socket.connected) {
            if (onConnected) {
                this.socket.once("connect", onConnected);
            }
            return this.socket;
        }

        this._token = token || this._token;

        console.log("🔌 Connecting Socket.IO:", SOCKET_URL);

        this.socket = io(SOCKET_URL, {
            auth: this._token ? { token: this._token } : undefined,
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelayMax: 5000,
            timeout: 20000,
        });

        this.socket.on("connect", () => {
            console.log("✅ Socket connected:", this.socket.id);
            this.connected = true;
            this.reconnectAttempts = 0;
            onConnected?.();
        });

        this.socket.on("disconnect", (reason) => {
            console.log("❌ Socket disconnected:", reason);
            this.connected = false;
        });

        this.socket.on("connect_error", (error) => {
            this.reconnectAttempts++;
            console.error(
                `❌ Socket error (attempt ${this.reconnectAttempts}):`,
                error?.message || error
            );
        });

        this.socket.on("reconnect", (attemptNumber) => {
            console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
            this.connected = true;
            this.reconnectAttempts = 0;
        });

        return this.socket;
    }

    joinSession(sessionId) {
        if (this.socket?.connected) {
            this.socket.emit("join-session", { sessionId });
            console.log(`✅ Joined session: ${sessionId}`);
        } else {
            console.warn("⚠️ Cannot join session - socket not connected yet");
        }
    }

    leaveSession(sessionId) {
        if (this.socket?.connected) {
            this.socket.emit("leave-session", { sessionId });
            console.log(`👋 Left session: ${sessionId}`);
        }
    }

    // ✅ NEW: Register user in session
    registerSessionUser(userId, sessionId) {
        if (this.socket?.connected) {
            this.socket.emit('register-session-user', { userId, sessionId });
            console.log(`✅ Registered session user: ${userId} in session: ${sessionId}`);
        } else {
            console.warn('⚠️ Cannot register session user - socket not connected yet');
        }
    }

    on(event, callback) {
        if (!this.socket) {
            console.warn(`⚠️ Cannot listen to '${event}' - socket not initialized`);
            return;
        }
        this.socket.on(event, callback);
    }

    off(event, callback) {
        if (!this.socket) return;
        this.socket.off(event, callback);
    }

    // ✅ FIXED: Primary method for sending personal like
    sendPersonalLike({ sessionId, fromUserId, toUserId, reaction = "like" }) {
        console.log("🔌 socketService.sendPersonalLike called:", {
            sessionId,
            fromUserId,
            toUserId,
            reaction,
            socketExists: !!this.socket,
            connected: this.socket?.connected,
            socketId: this.socket?.id
        });

        if (!sessionId) {
            console.error("❌ sendPersonalLike: missing sessionId");
            return false;
        }

        if (!toUserId) {
            console.error("❌ sendPersonalLike: missing toUserId");
            return false;
        }

        if (!fromUserId) {
            console.error("❌ sendPersonalLike: missing fromUserId");
            return false;
        }

        if (!this.socket?.connected) {
            console.error("❌ sendPersonalLike: socket not connected", {
                socketExists: !!this.socket,
                connected: this.socket?.connected
            });
            return false;
        }

        const payload = {
            sessionId,
            fromUserId,
            toUserId,
            reaction,
        };

        console.log("📤 Emitting send-personal-like:", payload);

        try {
            this.socket.emit("send-personal-like", payload);
            console.log("✅ send-personal-like emitted successfully");
            return true;
        } catch (error) {
            console.error("❌ Error emitting send-personal-like:", error);
            return false;
        }
    }

    // ✅ Alias method for compatibility
    sendPersonalReaction(reactionData) {
        console.log("🔌 socketService.sendPersonalReaction called:", reactionData);
        return this.sendPersonalLike({
            sessionId: reactionData.sessionId,
            fromUserId: reactionData.fromUserId,
            toUserId: reactionData.toUserId,
            reaction: reactionData.reaction || "like"
        });
    }

    emit(event, data) {
        if (!this.socket?.connected) {
            console.warn(`⚠️ Cannot emit '${event}' - socket not connected`, {
                socketExists: !!this.socket,
                connected: this.socket?.connected,
            });
            return false;
        }
        console.log(`📤 Socket emit: ${event}`, data);
        this.socket.emit(event, data);
        return true;
    }

    disconnect() {
        if (!this.socket) return;
        try {
            this.socket.disconnect();
        } finally {
            this.socket = null;
            this.connected = false;
        }
        console.log("🔌 Socket disconnected manually");
    }

    // ✅ Session emoji
    sendSessionEmoji({ sessionId, tableId, fromUserId, fromName, emoji }) {
        if (!sessionId || !emoji) return false;
        return this.emit("send-session-emoji", {
            sessionId,
            tableId,
            fromUserId,
            fromName,
            emoji
        });
    }

    // ✅ Legacy support
    sendTableReaction(reactionData) {
        return this.emit("send-session-emoji", reactionData);
    }

    // ✅ Convenience listeners
    onSessionEmoji(cb) {
        this.on("receive-session-emoji", cb);
    }

    offSessionEmoji(cb) {
        this.off("receive-session-emoji", cb);
    }

    onPersonalLike(cb) {
        // console.log("👂 Registering receive-personal-like listener");
        this.on("receive-personal-like", cb);
    }

    offPersonalLike(cb) {
        // console.log("🔇 Removing receive-personal-like listener");
        this.off("receive-personal-like", cb);
    }

    onUserJoinedTable(cb) {
        this.on("user-joined-table", cb);
    }

    offUserJoinedTable(cb) {
        this.off("user-joined-table", cb);
    }

    isConnected() {
        return !!this.socket?.connected;
    }

    isSocketConnected() {
        return this.isConnected();
    }

    getSocketId() {
        return this.socket?.id || null;
    }

    // ==================== BREAKOUT SOCKET EVENTS ====================

    /**
     * Listen for breakout invite
     */
    onBreakoutInvite(callback) {
        this.on("breakout-invite", callback);
    }

    offBreakoutInvite(callback) {
        this.off("breakout-invite", callback);
    }

    /**
     * Listen for breakout invite rejected (owner gets notified)
     */
    onBreakoutInviteRejected(callback) {
        this.on("breakout-invite-rejected", callback);
    }

    offBreakoutInviteRejected(callback) {
        this.off("breakout-invite-rejected", callback);
    }

    /**
     * Listen for participant joined breakout
     */
    onBreakoutParticipantJoined(callback) {
        this.on("breakout-participant-joined", callback);
    }

    offBreakoutParticipantJoined(callback) {
        this.off("breakout-participant-joined", callback);
    }

    /**
     * Listen for participant left breakout
     */
    onBreakoutParticipantLeft(callback) {
        this.on("breakout-participant-left", callback);
    }

    offBreakoutParticipantLeft(callback) {
        this.off("breakout-participant-left", callback);
    }

    /**
     * Listen for breakout ended
     */
    onBreakoutEnded(callback) {
        this.on("breakout-ended", callback);
    }

    offBreakoutEnded(callback) {
        this.off("breakout-ended", callback);
    }

    /**
     * Listen for breakout owner disconnected
     */
    onBreakoutOwnerDisconnected(callback) {
        this.on("breakout-owner-disconnected", callback);
    }

    offBreakoutOwnerDisconnected(callback) {
        this.off("breakout-owner-disconnected", callback);
    }

    /**
     * Listen for breakout owner reconnected
     */
    onBreakoutOwnerReconnected(callback) {
        this.on("breakout-owner-reconnected", callback);
    }

    offBreakoutOwnerReconnected(callback) {
        this.off("breakout-owner-reconnected", callback);
    }

    /**
     * Listen for breakout time warning (2 min before expiry)
     */
    onBreakoutTimeWarning(callback) {
        this.on("breakout-time-warning", callback);
    }

    offBreakoutTimeWarning(callback) {
        this.off("breakout-time-warning", callback);
    }

    /**
     * Emit register-breakout-user to join the breakout socket room
     */
    registerBreakoutUser(userId, sessionId, breakoutId) {
        if (this.socket?.connected) {
            this.socket.emit("register-breakout-user", {
                userId,
                sessionId,
                breakoutId,
            });
            console.log(`✅ Registered breakout user: ${userId} in breakout: ${breakoutId}`);
        } else {
            console.warn("⚠️ Cannot register breakout user - socket not connected");
        }
    }
}

const socketService = new SocketService();

export { socketService };
export default socketService;